import React, {useState, useEffect} from 'react';
import {Text, Icon} from '@albert-io/atomic';

import {WSStore, saveStatusType} from '../FreeResponseQuestion.types';

interface BottomBarProps {
  store: WSStore;
  wordCount: number;
}

const BottomBar = ({wordCount, store}: BottomBarProps) => (
  <span className='u-display_flex u-align-items_center u-mar-t_1'>
    <SaveMessage store={store} />
    <Text size='s' className='u-mar-l_auto u-color_slate-500'>
      Word Count: {wordCount}
    </Text>
  </span>
);

export default BottomBar;

// ---- BOTTOM BAR ELEMENTS------

interface MessageProps {
  store: WSStore;
}

const SaveMessage = ({store}: MessageProps) => {
  const [_, setTick] = useState(0);
  const status = store.getSaveStatus();
  const lastSaveTime = store.getLastSaveTime();
  const [offline, setOffline] = useState(false);

  useEffect(() => {
    const id = setInterval(() => {
      setTick((t) => t + 1); // trigger rerender each minute
    }, 60000);

    return () => {
      clearInterval(id);
    };
  }, [setTick]);

  useEffect(() => {
    const handleOffline = () => {
      setOffline(true);
    };
    const handleOnline = () => {
      setOffline(false);
    };

    global.addEventListener('offline', handleOffline);
    global.addEventListener('online', handleOnline);
    return () => {
      global.removeEventListener('offline', handleOffline);
      global.removeEventListener('online', handleOnline);
    };
  }, [setOffline]);

  if (offline) {
    return (
      <>
        <Icon color='danger' emphasis='lowest' icon={['fas', 'exclamation-triangle']} />
        <Text size='s' color='red' emphasis='high' className='u-mar-l_1'>
          {getUserFriendlyWarning(lastSaveTime)}
        </Text>
      </>
    );
  }

  if (status === saveStatusType.saveError) {
    return (
      <>
        <Icon color='danger' emphasis='lowest' icon={['fas', 'exclamation-triangle']} />
        <Text color='red' emphasis='high' className='u-mar-l_1'>
          There was an error saving your draft.
        </Text>
      </>
    );
  }

  if (status === saveStatusType.loadError) {
    return (
      <>
        <Icon color='danger' emphasis='lowest' icon={['fas', 'exclamation-triangle']} />
        <Text color='red' emphasis='high' className='u-mar-l_1'>
          There was an error loading your draft.
        </Text>
      </>
    );
  }

  if (status === saveStatusType.saving) {
    return (
      <>
        <Icon className='u-color_slate-500' icon={['far', 'sync']} />
        <Text size='s' className='u-mar-l_1 u-color_slate-500'>
          Saving
        </Text>
      </>
    );
  }

  if (status === saveStatusType.saved && !!lastSaveTime) {
    const now = new Date();
    const timeDiff = getUserFriendlyTimeDiff(lastSaveTime, now);
    return (
      <>
        <Icon className='u-color_slate-500' icon={['far', 'check']} />
        <Text size='s' className='u-mar-l_1 u-color_slate-500'>
          Saved, {timeDiff}
        </Text>
      </>
    );
  }

  return (
    <>
      <Icon className='u-color_slate-500' icon={['far', 'cloud-upload']} />
      <Text size='s' className='u-mar-l_1 u-color_slate-500'>
        Your work will be autosaved
      </Text>
    </>
  );
};
// -----BOTTOM BAR UTILS-------

const msPerMin = 1000 * 60;
const msPerHour = msPerMin * 60;
const msPerDay = msPerHour * 24;

const getUserFriendlyWarning = (date: Date | null) => {
  let formatedTime;
  if (date) {
    formatedTime = `Last saved ${date.toLocaleString()}`;
  }

  return `No network connection. ${formatedTime}`;
};

const getUserFriendlyTimeDiff = (date: Date, laterDate: Date) => {
  const utc1 = Date.UTC(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    date.getHours(),
    date.getMinutes()
  );
  const utc2 = Date.UTC(
    laterDate.getFullYear(),
    laterDate.getMonth(),
    laterDate.getDate(),
    laterDate.getHours(),
    laterDate.getMinutes()
  );

  const dayDifference = Math.floor((utc2 - utc1) / msPerDay);
  if (dayDifference > 0) {
    return `Last edit was ${dayDifference} days ago`;
  }

  const hourDifference = Math.floor((utc2 - utc1) / msPerHour);
  if (hourDifference > 0) {
    return `Last edit was ${hourDifference} hours ago`;
  }

  const minuteDifference = Math.floor((utc2 - utc1) / msPerMin);
  return `Last edit was ${minuteDifference} minutes ago`;
};
